import { ROLES } from '../constants/roles';
import { isAmMroConsecutiveBlocked } from './amMroConsecutiveApproval';

export const ISSUANCE_PENDING_APPROVAL_STATUSES = ['PENDING_STORE_APPROVAL', 'PENDING_AM_APPROVAL'];

/** Roles that can generate/download delivery challans on Issuance (store + procurement access set). */
export const ISSUANCE_CHALLAN_ROLES = [
  ROLES.STORE_EXECUTIVE,
  ROLES.STORE_INSPECTOR,
  ROLES.CA_MANAGER,
  ROLES.MRO_MANAGER,
  ROLES.CAMO_QM_MANAGER,
  ROLES.PLANNING_MANAGER,
  ROLES.ACCOUNTABLE_MANAGER,
  ROLES.AM_MRO,
  ROLES.SUPER_ADMIN
];

export function canManageIssuanceChallan(role) {
  return ISSUANCE_CHALLAN_ROLES.includes(role);
}

export function canReviewIssuance(role) {
  return [ROLES.STORE_INSPECTOR, ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(role);
}

export function isIssuancePendingApproval(issuance) {
  return ISSUANCE_PENDING_APPROVAL_STATUSES.includes(issuance?.approvalStatus);
}

export function isIssuanceRejected(issuance) {
  return issuance?.approvalStatus === 'REJECTED';
}

export function isIssuanceInventoryApplied(issuance) {
  if (issuance?.loanDirection === 'LOAN_OUT') {
    return issuance.approvalStatus === 'APPROVED';
  }
  return !issuance?.approvalStatus
    || issuance.approvalStatus === 'NOT_REQUIRED'
    || issuance.approvalStatus === 'APPROVED';
}

export function isRejectedIssuanceAwaitingRevision(issuance) {
  return isIssuanceRejected(issuance) && !isIssuanceInventoryApplied(issuance);
}

export function resolvePendingApprovalStatusAfterRejection(issuance) {
  if (issuance?.loanDirection === 'LOAN_OUT') {
    return issuance?.storeApprovedAt ? 'PENDING_AM_APPROVAL' : 'PENDING_STORE_APPROVAL';
  }
  return 'PENDING_STORE_APPROVAL';
}

export function isIssuanceReviewable(issuance) {
  return isIssuancePendingApproval(issuance) || isRejectedIssuanceAwaitingRevision(issuance);
}

export function isIssuanceSubmitter(issuance, userId) {
  if (!userId) return false;
  const uid = String(userId);
  // Match either original requester or issuer so Store Executive can still
  // revise after Store Manager forwarded (older records may have overwritten approvalRequestedBy).
  const candidates = [
    issuance?.approvalRequestedBy?._id || issuance?.approvalRequestedBy,
    issuance?.issuedBy?._id || issuance?.issuedBy
  ]
    .map((id) => String(id || ''))
    .filter(Boolean);
  return candidates.includes(uid);
}

export function canCorrectIssuance(role, issuance, userId) {
  if (!issuance || issuance.isArchived) return false;
  if (isIssuancePendingApproval(issuance)) {
    return canReviewIssuance(role);
  }
  if (isRejectedIssuanceAwaitingRevision(issuance)) {
    if (canReviewIssuance(role)) return true;
    return role === ROLES.STORE_EXECUTIVE && isIssuanceSubmitter(issuance, userId);
  }
  if (!canReviewIssuance(role)) return false;
  return ['ISSUED', 'PARTIAL_RETURN'].includes(issuance?.status);
}

export function canApprovePendingIssuance(role, issuance, user) {
  if (!canReviewIssuance(role)) return false;
  if (!issuance || issuance.isArchived) return false;

  const approvalStatus = isRejectedIssuanceAwaitingRevision(issuance)
    ? resolvePendingApprovalStatusAfterRejection(issuance)
    : issuance.approvalStatus;

  if (approvalStatus === 'PENDING_STORE_APPROVAL') return true;
  if (
    issuance.loanDirection === 'LOAN_OUT' &&
    approvalStatus === 'PENDING_AM_APPROVAL' &&
    [ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(role)
  ) {
    if (user && isAmMroConsecutiveBlocked(user, issuance?.storeApprovedBy)) return false;
    return true;
  }
  if (
    approvalStatus === 'PENDING_AM_APPROVAL' &&
    !issuance.loanDirection &&
    [ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(role)
  ) {
    if (user && isAmMroConsecutiveBlocked(user, issuance?.storeApprovedBy)) return false;
    return true;
  }
  return false;
}

export function requiresCorrectionNote(issuance) {
  if (!issuance) return false;
  if (isIssuanceReviewable(issuance)) return false;
  if (issuance.loanDirection === 'LOAN_OUT' && issuance.approvalStatus !== 'APPROVED') return false;
  return true;
}
